import "@xyflow/react/dist/style.css";

import { Affix, Button, Menu, Paper, Title } from "@mantine/core";
import { useFullscreen } from "@mantine/hooks";
import { IconSettingsUp } from "@tabler/icons-react";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Edge, Handle, MiniMap, Node, Position, ReactFlow, useEdgesState, useNodesState, useReactFlow } from "@xyflow/react";
import dagre from "dagre";
import { useCallback, useEffect, useRef } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { to, voiding } from "shared/utils/fns";
import { graphql } from "src/gql";
import { create } from "zustand";

const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
const nodeWidth = 200;
const nodeHeight = 200;

function getLayoutedElements(nodes: Node[], edges: Edge[]): { nodes: Node[]; edges: Edge[] } {
    dagreGraph.setGraph({ rankdir: useDirection.getState().dir });

    nodes.forEach((node) => {
        dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
    });

    edges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    nodes.forEach((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        node.position = {
            x: nodeWithPosition.x - nodeWidth / 2,
            y: nodeWithPosition.y - nodeHeight / 2,
        };
    });

    return { nodes, edges };
}

const useDirection = create<{ toggle: () => void; dir: "TD" | "LR" }>((set, get) => ({
    dir: "TD",
    toggle() {
        const dir = get().dir == "TD" ? "LR" : "TD";
        set({ dir });
    },
}));

const Page = () => {
    const { data } = useQuery(queries.root());
    const { toggle: toggleDirection, dir } = useDirection();

    const [nodes, setNodes, onNodesChange] = useNodesState([] as Node[]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([] as Edge[]);

    const onChildren = useCallback(
        (selfId: string, ids: string[]) => {
            setNodes((prevNodes) => {
                const diffs = ids.filter((id) => prevNodes.every((node) => node.id !== id));
                const newNodes = diffs.map((id) => ({
                    id,
                    position: { x: 0, y: 0 },
                    data: { topicId: id, setChildren: onChildren },
                    type: "topic",
                }));

                const nextNodes = prevNodes.concat(newNodes);
                const layout = getLayoutedElements(nextNodes, edges);
                return layout.nodes;
            });

            setEdges((prevEdges) => {
                const newEdges = ids.map((id) => ({
                    id: `${selfId}-${id}`,
                    source: selfId,
                    target: id,
                    sourceHandle: null,
                    targetHandle: null,
                }));
                const layout = getLayoutedElements(nodes, [...prevEdges, ...newEdges]);
                return layout.edges;
            });
        },
        [setNodes, setEdges, dir],
    );

    useEffect(() => {
        if (!data) return;
        if (nodes.length > 0) return;
        setNodes([
            {
                id: data.rootTopic.id,
                position: { x: 0, y: 0 },
                type: "topic",
                data: { topicId: data.rootTopic.id, setChildren: onChildren },
            },
        ]);
        onChildren(data.rootTopic.id, data.rootTopic.children.map(to("id")));
    }, [data, dir]);

    const { ref, toggle: toggleFullscreen } = useFullscreen();

    return (
        <>
            <Affix position={{ bottom: 20, right: 20 }}>
                <Menu>
                    <Menu.Target>
                        <Button leftSection={<IconSettingsUp size={16} />}>Configure</Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Item onClick={voiding(toggleFullscreen)}>Fullscreen</Menu.Item>
                        <Menu.Item
                            onClick={() => {
                                setNodes([]);
                                setEdges([]);

                                toggleDirection();
                            }}
                        >
                            Toggle Direction
                        </Menu.Item>
                    </Menu.Dropdown>
                </Menu>
            </Affix>
            <Content>
                <Content.Heading>
                    <Title order={3}>Organization Topics Diagram</Title>
                </Content.Heading>
            </Content>

            <ReactFlow
                fitView
                ref={ref}
                nodes={nodes}
                nodeTypes={{ topic: TopicNode }}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                attributionPosition="top-right"
            >
                <AutoFit />
                <MiniMap zoomable pannable position="bottom-left" />
            </ReactFlow>
        </>
    );
};

const AutoFit = () => {
    const rf = useReactFlow();
    useEffect(() => {
        void rf.fitView();
        rf.setNodes(getLayoutedElements(rf.getNodes(), rf.getEdges()).nodes);
    }, [rf.getNodes().length]);

    return null;
};

const TopicNode = (props: { data: { topicId: string; setChildren: (selfId: string, childIds: string[]) => void } }) => {
    const { dir } = useDirection();
    const { data } = useQuery({ ...queries.topic(props.data.topicId) });
    const didFire = useRef(false);

    useEffect(() => {
        if (!data) return;
        if (didFire.current) return;

        props.data.setChildren(props.data.topicId, data.topic.children.map(to("id")));
        didFire.current = true;
    }, [data]);

    return (
        <>
            <Handle type="target" position={dir == "TD" ? Position.Top : Position.Left} />
            <Link to={"/organization/topics/$topicId"} params={{ topicId: props.data.topicId }}>
                <Paper withBorder p="xl" style={{ borderColor: "var(--mantine-primary-color-filled)", maxWidth: "200px" }} radius="xl">
                    {data?.topic.name}
                </Paper>
            </Link>
            <Handle type="source" position={dir == "TD" ? Position.Bottom : Position.Right} />
        </>
    );
};

const gql = {
    topic: graphql(`
        query TopicVisTopic($id: UUID!) {
            topic(topicId: $id) {
                id
                name
                children {
                    id
                }
            }
        }
    `),
    root: graphql(`
        query TopicVisRoot {
            rootTopic {
                id
                name
                children {
                    id
                }
            }
        }
    `),
};

const queries = {
    topic: (id: string) =>
        queryOptions({
            queryKey: ["devtools", "topic-vis", id],
            queryFn: () => httpPostGraphql(gql.topic, { id }),
        }),
    root: () =>
        queryOptions({
            queryKey: ["devtools", "topic-vis", "root"],
            queryFn: () => httpPostGraphql(gql.root, {}),
        }),
};

/** @public */
export const Route = createFileRoute("/_auth/_dev/devtools/topics-vis")({
    component: Page,
});
